/* eslint-disable no-console */
import AnalyticsWebClient, {
  CallbackFn,
  CommonEvent,
  envType,
  EventAttributes,
  originType,
  ScreenEvent,
  tenantType,
  userType,
} from '@atlassiansox/analytics-web-client';
import { getEnvironment } from '@atlassian/temp-shared/environment';
import { DEVELOPMENT, LOCAL, PRODUCTION, STAGING } from '@atlassian/temp-shared/environment-constants';
import {
  AnalyticsClient,
  AnalyticsEvent,
  AnalyticsOptions,
  ANALYTICS_PRODUCT_DEFAULT,
  ANALYTICS_SUBPRODUCT_DEFAULT,
  AtlassianAccountIdProvider,
  GlobalPageProperties,
  RolesProvider,
} from './analytics-client';

export const getEnvType = () => {
  switch (getEnvironment()) {
    case LOCAL:
      return envType.LOCAL;
    case DEVELOPMENT:
      return envType.DEV;
    case STAGING:
      return envType.STAGING;
    case PRODUCTION:
      return envType.PROD;
    default:
      return envType.STAGING;
  }
};

class GasAnalyticsClient implements AnalyticsClient {
  private analyticsClient: any;
  private hasSentSetupWarning = false;
  private hasSentException = false;
  private roles?: string[] = undefined;
  private aaidProvider!: AtlassianAccountIdProvider;
  private rolesProvider!: RolesProvider;
  private globalPageProperties!: GlobalPageProperties;

  // Other sub-apps can update the subproduct
  constructor() {
    const apiHost =
      getEnvType() === envType.PROD
        ? 'api-private.atlassian.com/gasv3/api/v1'
        : 'api-private.stg.atlassian.com/gasv3/api/v1';
    this.analyticsClient = new AnalyticsWebClient(
      {
        env: getEnvType(),
        origin: originType.WEB,
        product: ANALYTICS_PRODUCT_DEFAULT,
        subproduct: ANALYTICS_SUBPRODUCT_DEFAULT,
      },
      { apiHost }
    );

    // TODO: COMMIT-7497 Find org and workspace ID
    // this.analyticsClient.setOrgInfo('dummy-fc0877ef-3ffd-4a22-81b9-68b6884e6a96');
    // this.analyticsClient.setWorkspaceInfo('workspaceAri / workspaceID');

    this.analyticsClient.setTenantInfo(tenantType.NONE);
  }

  sendScreenEvent = async (event: ScreenEvent, callback?: CallbackFn) => {
    try {
      const enrichedEvent = this.enrichScreenEvent(event);

      if (await this.shouldSendMessage()) {
        await this.analyticsClient.sendScreenEvent(enrichedEvent, callback);
      }
    } catch (e) {
      this.handleException(e as Error, 'Error sending screen event');
    }
  };

  sendTrackEvent = async (event: AnalyticsEvent, callback?: CallbackFn) => {
    try {
      const enrichedEvent = this.enrichCommonEvent(event);

      if (await this.shouldSendMessage()) {
        await this.analyticsClient.sendTrackEvent(enrichedEvent, callback);
      }
    } catch (e) {
      this.handleException(e as Error, 'Error sending analytics event');
    }
  };

  sendUIEvent = async (event: AnalyticsEvent, callback?: CallbackFn) => {
    try {
      const enrichedEvent = this.enrichCommonEvent(event);

      if (await this.shouldSendMessage()) {
        await this.analyticsClient.sendUIEvent(enrichedEvent, callback);
      }
    } catch (e) {
      this.handleException(e as Error, 'Error sending analytics event');
    }
  };

  sendOperationalEvent = async (event: AnalyticsEvent, callback?: CallbackFn) => {
    try {
      const enrichedEvent = this.enrichCommonEvent(event);

      if (await this.shouldSendMessage()) {
        await this.analyticsClient.sendOperationalEvent(enrichedEvent, callback);
      }
    } catch (e) {
      this.handleException(e as Error, 'Error sending analytics event');
    }
  };

  setSubproduct = (subproduct: string) => {
    this.analyticsClient.setSubproduct(subproduct);
  };

  setAtlassianAccountId = (aaid: string) => {
    this.analyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, aaid);
  };

  setup = async (aaidProvider: AtlassianAccountIdProvider, options?: AnalyticsOptions) => {
    try {
      this.aaidProvider = aaidProvider;

      if (options && options.subproduct) {
        this.setSubproduct(options.subproduct);
      }
    } catch (e) {
      this.handleException(e as Error, 'Error setting up analytics');
    }
  };

  setupRoles = async (rolesProvider: RolesProvider) => {
    this.rolesProvider = rolesProvider;

    try {
      const roles = await this.rolesProvider();
      this.setRoles(roles);
    } catch (e) {
      this.handleException(e as Error, 'Unable to setup the AAID Provider');
    }
  };

  shouldSendMessage = async () => {
    try {
      if (this.aaidProvider) {
        const aaid = await this.aaidProvider();
        this.setAtlassianAccountId(aaid);

        if (this.rolesProvider) {
          const roles = await this.rolesProvider();
          this.setRoles(roles);
        }
        return true;
      }
    } catch (e) {
      this.handleException(e as Error, 'Unable to setup the AAID Provider');
    }

    if (getEnvType() !== envType.PROD && !this.hasSentSetupWarning) {
      global.console.warn('[DEV NOTE] Please setup analytics in your application before trying to send events');

      this.hasSentSetupWarning = true;
    }
    return false;
  };

  setGlobalPageProperties = (properties: GlobalPageProperties) => {
    this.globalPageProperties = properties;
  };

  startUIViewedEvent = async () => {
    try {
      if (await this.shouldSendMessage()) {
        this.analyticsClient.startUIViewedEvent();
      }
    } catch (e) {
      this.handleException(e as Error, 'Error starting UI viewed event');
    }
  };

  getAnalyticsClient = () => this.analyticsClient;

  private enrichCommonEvent = (event: AnalyticsEvent): CommonEvent => {
    const enrichedAttributes = this.enrichAttributes(event.attributes);

    return {
      source: this.globalPageProperties && this.globalPageProperties.source,
      ...event,
      attributes: {
        ...(this.globalPageProperties && this.globalPageProperties.pageAttributes),
        ...enrichedAttributes,
      },
    } as CommonEvent;
  };

  private enrichScreenEvent = (event: ScreenEvent): ScreenEvent => {
    const enrichedAttributes = this.enrichAttributes(event.attributes);

    return {
      ...event,
      attributes: {
        ...enrichedAttributes,
      },
    };
  };

  private enrichAttributes = (attributes?: EventAttributes) => {
    const roles = this.roles ? this.roles : undefined;

    return {
      ...attributes,
      roles,
    };
  };

  private setRoles = (roles: string[]) => {
    this.roles = roles;
  };

  private handleException = (error: Error, errNote: string) => {
    if (getEnvType() !== envType.PROD && !this.hasSentException) {
      global.console.warn(`[DEV NOTE] ${errNote}: ${error}`);

      this.hasSentException = true;
    }
  };
}

export default GasAnalyticsClient;
